import React, { useState, useEffect } from "react";
import { MapPin, Instagram, Linkedin, Stethoscope, X } from "lucide-react";
import { motion } from "framer-motion";
import Typewriter from "typewriter-effect";

export default function HospiceMejorJuntos() {
    const [showModal, setShowModal] = useState(false);
    const [igPosts, setIgPosts] = useState([]);

    /**
     * ----------------------------------------------------------------------
     * INSTAGRAM FETCH
     * ----------------------------------------------------------------------
     * Llama a /api/instagram (Next.js o similar) que actúe como proxy
     * a la Instagram Graph API para NO exponer el token en el cliente.
     * Debe devolver algo así:
     *   [
     *     { media_url, permalink, caption, media_type },
     *     …
     *   ]
     * Sustituye esta lógica si prefieres incrustar imágenes manualmente.
     */
    useEffect(() => {
        async function fetchIg() {
            try {
                const res = await fetch("/api/instagram");
                if (!res.ok) throw new Error("IG fetch failed");
                const data = await res.json();
                setIgPosts(data.slice(0, 3));
            } catch (err) {
                console.error(err);
            }
        }
        fetchIg();
    }, []);

    /* Placeholder en desarrollo */
    const placeholderPosts = [
        { media_url: "/imagen1.png", permalink: "#" },
        { media_url: "/imagen2.png", permalink: "#" },
        { media_url: "/imagen1.png", permalink: "#" },
    ];

    return (
        <div className="font-sans relative bg-[#fdf9f6] text-gray-800">
            {/* ---------------- Modal Donaciones ---------------- */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
                    <div className="bg-white rounded-xl shadow-xl p-8 relative max-w-md w-full">
                        <button
                            className="absolute top-4 right-4 text-gray-500"
                            onClick={() => setShowModal(false)}
                        >
                            <X size={24} />
                        </button>
                        <h2 className="text-2xl font-bold text-[#5494ad] mb-4 text-center">
                            Datos para Donación
                        </h2>
                        <p>
                            <strong>ASOCIACION CIVIL MEJOR JUNTOS</strong>
                        </p>
                        <p>CVU: 0000003100068710860811</p>
                        <p>Alias: hospicemejorjuntos</p>
                        <p>CUIT/CUIL: 30718849086</p>
                        <p>Mercado Pago</p>
                    </div>
                </div>
            )}

            {/* ---------------- Navbar ---------------- */}
            <nav className="flex justify-between items-center p-4 bg-white bg-opacity-90 backdrop-blur-md shadow-md fixed w-full z-40">
                <div className="flex items-center space-x-4">
                    <img src="/mejor-juntos.png" alt="Hospice Mejor Juntos" className="h-12" />
                    <span className="text-2xl font-extrabold text-[#5494ad] tracking-wide">
                        Hospice Mejor Juntos
                    </span>
                </div>
                <div className="flex space-x-10 text-lg font-medium items-center">
                    <a
                        href="#quehacemos"
                        className="hover:text-[#5494ad] transition-all relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-[#5494ad] hover:after:w-full after:transition-all"
                    >
                        Qué hacemos
                    </a>
                    <a
                        href="#comoayudar"
                        className="hover:text-[#5494ad] transition-all relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-[#5494ad] hover:after:w-full after:transition-all"
                    >
                        Cómo ayudar
                    </a>
                    <a
                        href="#nosotros"
                        className="hover:text-[#5494ad] transition-all relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-[#5494ad] hover:after:w-full after:transition-all"
                    >
                        Nosotros
                    </a>
                    <button
                        onClick={() => setShowModal(true)}
                        className="bg-gradient-to-r from-[#76b6c7] to-[#5494ad] text-white text-lg py-3 px-6 rounded-full shadow-xl hover:scale-110 hover:shadow-2xl transition-all focus:ring-4 focus:ring-[#76b6c7]/50"
                    >
                        Donar
                    </button>
                </div>
            </nav>

            {/* ---------------- Hero ---------------- */}
            <div>
                <div
                    className="-mt-20 md:-mt-24 relative h-[100vh] bg-cover …"
                    style={{ backgroundImage: `url('/hero-banner.png')` }}
                >
                    <div className="absolute inset-0 bg-[#5494ad]/60 flex items-center justify-center">
                        <motion.h1
                            initial={{ opacity: 0, y: -50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1 }}
                            className="text-white text-5xl font-bold text-center p-4 drop-shadow-lg"
                        >
                            Cuidar es amar,
                            <br />
                            y siempre, es Mejor Juntos
                        </motion.h1>
                    </div>
                </div>
            </div>

            {/* ---------------- Objetivo, Misión, Visión ---------------- */}
            <section className="py-24 px-4 max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
                {[
                    {
                        title: "Nuestro Objetivo",
                        text:
                            "Ser una asociación sin fines de lucro que cuida y acompaña de forma gratuita a quienes transitan el final de sus vidas, en zona oeste de CABA.",
                    },
                    {
                        title: "Misión",
                        text:
                            "Cuidar y acompañar a personas y familias en situación de enfermedades avanzadas y bajos recursos, desde la fe cristiana evangélica.",
                    },
                    {
                        title: "Visión",
                        text:
                            "Acompañamiento domicilio, Telefónico, Hospitalario, Una casa propia de cuidados.",
                    },
                ].map((item, index) => (
                    <motion.div
                        key={index}
                        className="bg-white rounded-3xl shadow-2xl p-8 text-center hover:scale-105 transition-all"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="text-2xl font-bold text-[#5494ad] mb-4">{item.title}</h3>
                        <p className="text-lg">{item.text}</p>
                    </motion.div>
                ))}
            </section>

            {/* ---------------- Qué hacemos ---------------- */}
            <section id="quehacemos" className="py-24 bg-[#f5fafd] px-4">
                <div className="max-w-5xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-[#5494ad] mb-12">Qué hacemos</h2>
                    <div className="text-xl min-h-40 text-[#333] leading-relaxed">
                        <Typewriter
                            onInit={(typewriter) => {
                                typewriter
                                    .typeString(
                                        "Nacemos de un sueño hace muchos años, con el deseo profundo de acompañar y cuidar."
                                    )
                                    .pauseFor(800)
                                    .typeString(
                                        "<br/>Hoy, millones de personas necesitan cuidados paliativos cada año en el mundo."
                                    )
                                    .pauseFor(800)
                                    .typeString(
                                        "<br/>Buscamos brindar dignidad, alivio, contención y acompañamiento integral."
                                    )
                                    .start();
                            }}
                            options={{ delay: 50, cursor: "|", loop: false }}
                        />
                    </div>
                </div>
            </section>

            {/* ---------------- Cómo ayudar ---------------- */}
            <section id="comoayudar" className="py-24 bg-white px-4">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-4xl font-bold text-center text-[#5494ad] mb-12">
                        Cómo ayudar
                    </h2>
                    <div className="grid md:grid-cols-2 gap-10 mb-10">
                        <ul className="list-disc list-inside text-lg space-y-3">
                            <li>
                                Orando por quienes acompañamos: tu oración es un sostén fundamental
                                para los pacientes, las familias y el equipo de Hospice.
                            </li>
                            <li>
                                Formándote para acompañar: si tenés el deseo, podés capacitarte para
                                brindar acompañamiento y contención.
                            </li>
                            <li>
                                Desde tu profesión: si sos profesional, podés aportar tus
                                conocimientos y experiencia en distintas áreas.
                            </li>
                            <li>
                                Aportando recursos materiales: según las necesidades que vayan
                                surgiendo, también es posible colaborar con insumos o materiales.
                            </li>
                        </ul>
                        <div className="flex flex-col justify-center gap-6">
                            <button
                                onClick={() => setShowModal(true)}
                                className="bg-[#5494ad] text-white text-lg py-3 rounded-xl shadow-lg hover:scale-105 transition"
                            >
                                Quiero ayudar económicamente
                            </button>
                            <a
                                href="https://forms.gle/9FPjPNZPrx47ajpG6"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="border border-[#5494ad] text-[#5494ad] text-lg py-3 rounded-xl shadow-lg text-center hover:scale-105 transition"
                            >
                                Quiero colaborar
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- Nosotros ---------------- */}
            <section id="nosotros" className="py-24 bg-[#f5fafd] px-4">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10">
                    {/* Imagen a la izquierda */}
                    <img
                        src="/hero-hospice.png"               /* tu ruta real */
                        alt="Equipo Hospice Mejor Juntos"
                        className="w-full md:w-1/2 rounded-3xl shadow-2xl object-cover"
                    />

                    {/* Texto a la derecha */}
                    <div className="flex-1 text-center md:text-left">
                        <h2 className="text-4xl font-bold text-[#5494ad] mb-8">Nosotros</h2>
                        <p className="text-lg leading-relaxed">
                            Somos una asociación sin fines de lucro que acompaña a personas en el
                            final de sus vidas, a aquellas con escasos recursos y con poca red de
                            apoyo.<br /><br />
                            Ofrecemos cuidado integral: físico, emocional, social y espiritual.
                            <br /><br />
                            Hoy, más que nunca, creemos que estar ahí, como dijo Jesús en Getsemaní,
                            es nuestro llamado.
                        </p>
                    </div>
                </div>
            </section>


            {/* -------- Banner Iglesia La Puerta Abierta (sin márgenes blancos) -------- */}
            <section className="overflow-hidden bg-white" >             {/* ⬅ sin py-*, sin px-* */}
                <div className="flex flex-col md:flex-row items-stretch">

                    {/* Columna de texto – padding interno propio */}
                    <div className="w-full md:w-1/2 p-8 sm:p-12 md:p-16 flex flex-col justify-center text-center md:text-left">
                        <h3 className="text-3xl font-bold text-[#5494ad] mb-4">
                            Iglesia La Puerta Abierta
                        </h3>
                        <p className="text-lg mb-6">
                            Con el abrazo de la Iglesia La Puerta Abierta, nuestro respaldo y
                            bendición.
                        </p>
                        <a
                            href="https://lapuertaabierta.org/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#5494ad] text-white py-3 px-6 rounded-full shadow-lg hover:scale-105 transition
             inline-block w-fit self-center md:self-start"
                        >
                            Conocé la Iglesia
                        </a>
                    </div>

                    {/* Columna de imagen – ocupa toda la mitad derecha, sin bordes */}
                    <div className="w-full md:w-1/2 min-h-[350px] md:min-h-[450px]">
                        <img
                            src="/iglesia.png"
                            alt="Iglesia La Puerta Abierta"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>




            {/* ---------------- Frase ---------------- */}
            <section className="py-24 bg-[#f5fafd] text-center">
                <h2 className="text-3xl font-bold text-[#5494ad] mb-6">
                    "Velad conmigo" no es solo una invitación… es nuestra tarea.
                </h2>
            </section>

            {/* ---------- Últimas novedades (embeds más altos) ---------- */}
            <section className="py-24 bg-[#f5fafd]">
                <h2 className="text-4xl font-bold text-center text-[#5494ad] mb-12">
                    Últimas novedades
                </h2>

                <div className="flex flex-col md:flex-row justify-center gap-8">
                    {[
                        "DKvH2aRPc3f",  // Post 1
                        "DKVp4xdv98X",  // Post 2
                        "DLTb_UkPTiC",  // Reel 3
                    ].map((code) => (
                        <iframe
                            key={code}
                            src={`https://www.instagram.com/p/${code}/embed`}
                            /* 👇 altura forzada a 600 px (tailwind) */
                            className="w-full md:w-1/3 max-w-md h-[500px] border-0 overflow-hidden"
                            allowTransparency
                            allowFullScreen
                            scrolling="no"
                            loading="lazy"
                            title={`IG-${code}`}
                        />
                    ))}
                </div>
            </section>



            {/* ---------------- Dónde estamos ---------------- */}
            <section className="py-24 bg-[#f5fafd] px-4">
                <motion.div
                    className="max-w-4xl mx-auto text-center"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-4xl font-bold text-[#5494ad] mb-12">
                        Dónde estamos
                    </h2>
                    <p className="text-lg mb-4 flex justify-center items-center">
                        <MapPin className="w-6 h-6 mr-2 text-[#5494ad]" /> Irigoyen 1208,
                        Comuna 10, CABA, Argentina
                    </p>
                    <iframe
                        className="w-full h-96 rounded-2xl shadow-lg mb-6"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3282.529425234533!2d-58.47694242350019!3d-34.639279959146184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95bcc948c6623e87%3A0x4e87c5b8f72d90ec!2sIrigoyen%201208%2C%20C1407CBA%20CABA!5e0!3m2!1ses-419!2sar!4v1718113712345!5m2!1ses-419!2sar"
                        allowFullScreen
                        loading="lazy"
                    />
                    <div className="flex justify-center space-x-6 text-[#5494ad] text-3xl mb-4">
                        <a
                            href="https://www.instagram.com/hospicemejorjuntos/"
                            target="_blank"
                        >
                            <Instagram />
                        </a>
                        <a
                            href="https://www.linkedin.com/company/hospice-mejor-juntos/posts/?feedView=all"
                            target="_blank"
                        >
                            <Linkedin />
                        </a>
                    </div>
                    <p className="text-lg text-[#5494ad] font-semibold">
                        hospicemejorjuntos@gmail.com
                    </p>
                </motion.div>
            </section>

            {/* ---------------- Footer ---------------- */}
            <section className="py-8 bg-[#fdf9f6] text-center text-gray-600">
                Asociación Civil Mejor Juntos desde el 16 de enero 2025
            </section>
            <footer className="py-6 text-center text-gray-500 text-sm">
                © {new Date().getFullYear()} Hospice Mejor Juntos. Todos los derechos
                reservados.
            </footer>

            {/* ---------------- Botón flotante WhatsApp ---------------- */}
            <a
                href="https://api.whatsapp.com/send?phone=5491123380977&text=%C2%A1Hola!%20Te%20comunicaste%20con%20Hospice%20Mejor%20Juntos%20%C2%BFEn%20que%20te%20podemos%20ayudar?"
                target="_blank"
                className="fixed bottom-6 right-6 bg-[#5494ad] p-4 rounded-full shadow-lg hover:scale-110 transition-transform"
            >
                <Stethoscope className="text-white w-8 h-8" />
            </a>
        </div>
    );
}
